/**
 * JobDetail.jsx — Full detail panel for a selected job (MUI)
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Chip, Stack, Divider, TextField, Table, TableHead, TableBody, TableRow, TableCell, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Link, FormControlLabel, Checkbox, Badge, Select, FormControl, InputLabel, List, ListItemButton, ListItemText } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { fmtD, fmtC, fmtT, capitalize, formatStatus, STATUS_COLORS, INVOICE_STATUS_COLORS } from '../../lib/utils';
import { ALL_STATUSES, TRANSITION_LABELS } from '../../lib/statusTransitions';
import { navigateToContact, navigateToInvoice } from '../../lib/crossNav';
import { openEmail } from '../../lib/emailTemplates';
import { getPaymentMethodLabel } from '../../lib/paymentMethods';

import { normalizeSnapshot } from '../../lib/calcEngine';
import { getAutoStatusIfValid, getNextShootDate } from '../../lib/autoStatus';
import JobIdentityBlockDialog from '../quotes/JobIdentityBlockDialog';
import { useUpdateJob, useDeleteJob } from '../../hooks/useJobs';
import { useSettings } from '../../hooks/useSettings';
import { useInvoices, useInvoicesByJob, useUpdateInvoice } from '../../hooks/useInvoices';
import { useTimeEntriesByJob, useDeleteTimeEntry } from '../../hooks/useTimeEntries';
import { useExpensesByJob, useDeleteExpense } from '../../hooks/useExpenses';
import { useRoster } from '../../hooks/useRoster';
import { useContacts } from '../../hooks/useContacts';
import { useGcalConfig, useGcalStatus, useSyncToGcal } from '../../hooks/useGcalSync';
import { useXeroStatus, useSyncInvoiceToXero, useUploadInvoicePdfToXero } from '../../hooks/useXero';
import { pushInvoicePdfToXero } from '../../lib/invoicePdfSync';
import { useToast } from '../layout/Toast';
import JobPipeline from './JobPipeline';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import InvoiceForm from '../invoices/InvoiceForm';
import LinkInvoiceModal from '../invoices/LinkInvoiceModal';
import TimeEntryForm from '../payroll/TimeEntryForm';
import ExpenseForm from '../expenses/ExpenseForm';
import UserAvatar from '../shared/UserAvatar';
import { getUserColor } from '../../lib/userColors';
import HandoffNotes from './HandoffNotes';
import JobLedgerPanel from './JobLedgerPanel';
import StatusBackslideModal from './StatusBackslideModal';
import { isBackslide } from '../../lib/payoutEngine';
import { api } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { useMobile } from '../../hooks/useMobile';
import { useRBAC } from '../../hooks/useRBAC';
import { useFocus } from '../../hooks/useFocus';
import { useQuotes, useUpdateQuote } from '../../hooks/useQuotes';
import { syncQuoteToJobStatus } from '../../lib/quoteSync';
import { splitDeliverables } from '../../lib/jobDeliverables';

const HOMEBASE_FALLBACK = '570 Bolinas Way, Unit 102, Goleta CA 93117';

// Cache travel times so we don't re-query Google on every render
const travelCache = new Map();

function fetchTravelMins(origin, destination) {
  if (!origin || !destination) return Promise.resolve(null);
  const key = `${origin}|||${destination}`;
  if (travelCache.has(key)) return Promise.resolve(travelCache.get(key));
  if (!window.google?.maps?.DistanceMatrixService) return Promise.resolve(null);
  return new Promise((resolve) => {
    const svc = new window.google.maps.DistanceMatrixService();
    svc.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      },
      (response, status) => {
        if (status === 'OK' && response?.rows?.[0]?.elements?.[0]?.status === 'OK') {
          const mins = Math.round(response.rows[0].elements[0].duration.value / 60);
          travelCache.set(key, mins);
          resolve(mins);
        } else {
          travelCache.set(key, null);
          resolve(null);
        }
      },
    );
  });
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Delete confirm for both the condensed and full views. A delete is no longer
 * final: the server snapshots the job and everything that goes with it into
 * Trash first (server/lib/trash.js), so say what moves and how to get it back.
 */
function DeleteJobDialog({ open, onClose, onConfirm, pending, invoices, timeEntries, expenses, blocked }) {
  // A null count means the viewer only reads their own rows (time/expenses
  // below full, ledger below payroll:full), so a number would undercount.
  const goes = [
    invoices > 0 && plural(invoices, 'invoice', 'invoices'),
    'Any payout lines (pay split earnings)',
    timeEntries == null ? 'Any time entries logged on it, not just yours' : timeEntries > 0 && plural(timeEntries, 'time entry', 'time entries'),
    expenses == null ? 'Any expenses logged on it, not just yours' : expenses > 0 && plural(expenses, 'expense', 'expenses'),
  ].filter(Boolean);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle color="error">Delete this job?</DialogTitle>
      <DialogContent>
        {blocked && (
          <Typography variant="body2" color="error" sx={{ mb: 1.5, fontWeight: 600 }}>{blocked}</Typography>
        )}
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          It moves to Trash for 30 days. Until then an admin can restore it from Settings ▸ Trash; after that it is gone for good.
        </Typography>
        <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>Goes to Trash with it:</Typography>
        {goes.map((g) => <Typography key={g} variant="body2" sx={{ pl: 1 }}>• {g}</Typography>)}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Linked quotes are kept but unlinked from the job, and its calendar events are cancelled. A restore re-links the quotes and re-creates the events.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={pending || !!blocked}>Move to Trash</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function JobDetail({ job, onClose, onRefresh }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile } = useAuth();
  const { isMobile } = useMobile();
  const { hasAction, hasAccess } = useRBAC();
  const { isHidden } = useFocus();
  const updateMutation = useUpdateJob();
  const deleteMutation = useDeleteJob();
  const { data: allQuotes = [] } = useQuotes();
  const updateQuoteMut = useUpdateQuote();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showVoidReason, setShowVoidReason] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidNotifyCrew, setVoidNotifyCrew] = useState(false);
  const [backslidePending, setBackslidePending] = useState(null); // { newStatus, preview }
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceFormType, setInvoiceFormType] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);

  // ── Inline edit state for quick-add condensed view ──
  const [quickEdit, setQuickEdit] = useState(false);
  const [qf, setQf] = useState({});
  const [qfContactId, setQfContactId] = useState(null);
  // Server refused a client change on a committed job (409 job_identity_locked).
  // Holds { block, payload } so the override can re-send the same edit.
  const [identityBlock, setIdentityBlock] = useState(null);
  const [qfContactSearch, setQfContactSearch] = useState('');
  const [qfShowDropdown, setQfShowDropdown] = useState(false);

  const startQuickEdit = () => {
    setQf({
      clientName: job.clientName || '', contactEmail: job.contactEmail || '', contactPhone: job.contactPhone || '',
      jobType: job.jobType || '', shootDate: job.shootDate || '', revenue: job.revenue || '', location: job.location || '',
      workflowType: job.workflowType || 'standard',
    });
    setQfContactId(job.contactId || null);
    setQuickEdit(true);
  };
  const cancelQuickEdit = () => { setQuickEdit(false); setQfContactSearch(''); setQfShowDropdown(false); };

  const { data: settings } = useSettings();
  const homebase = settings?.homebaseAddress || HOMEBASE_FALLBACK;
  const [travelMins, setTravelMins] = useState(null);
  const travelReqRef = useRef('');
  useEffect(() => {
    const dest = job?.location;
    const reqKey = `${homebase}|||${dest}`;
    if (!dest || !homebase) { setTravelMins(null); return; }
    if (travelReqRef.current === reqKey) return;
    travelReqRef.current = reqKey;
    fetchTravelMins(homebase, dest).then((m) => {
      if (travelReqRef.current === reqKey) setTravelMins(m);
    });
  }, [homebase, job?.location]);
  const { data: roster = [] } = useRoster();
  const { data: allContacts = [] } = useContacts();
  const { data: gcalConfig } = useGcalConfig();
  const { data: gcalStatus } = useGcalStatus(job?.id);
  const syncToGcalMut = useSyncToGcal();
  const gcalEnabled = gcalConfig?.configured === true;
  const { data: xeroStatus } = useXeroStatus();
  const xeroSyncMutation = useSyncInvoiceToXero();
  const uploadXeroPdfMutation = useUploadInvoicePdfToXero();
  const xeroConnected = xeroStatus?.connected === true;
  const { data: jobInvoices = [], isLoading: invoicesLoading } = useInvoicesByJob(job?.id);
  const updateInvoiceMutation = useUpdateInvoice();
  // Bulk invoices covering this job through job_ids (their own job_id is
  // NULL, so useInvoicesByJob can't see them). Display-only — deliberately
  // NOT merged into jobInvoices: autoStatus and the Invoiced/Received totals
  // compare per-job amounts, and a whole-bundle amount would corrupt both.
  const { data: allInvoicesGlobal = [] } = useInvoices();
  const coveringBulkInvoices = allInvoicesGlobal.filter(
    (inv) => inv.status !== 'void' && Array.isArray(inv.jobIds) && inv.jobIds.includes(job?.id)
  );

  // No auto-check on open. This used to fire useCheckXeroPaymentsByJob here,
  // which spends one Xero API call per unpaid invoice every time anyone merely
  // opens a job — its result was never even read, and nginx's 60s timeout made
  // the browser re-fire it every minute on a slow response. It was the largest
  // single amplifier of the 2026-07-27 quota outage. The background poller
  // (server/index.js) covers this every 30 minutes.

  // ── Auto-status engine: advance status based on invoices/dates ──
  const autoStatusRef = useRef('');
  useEffect(() => {
    if (!job?.id || invoicesLoading) return;
    const key = `${job.id}:${job.status}:${job.revenue}:${jobInvoices.map(i => `${i.status}/${i.amount}/${i.sentAt ? 1 : 0}`).join(',')}`;
    if (autoStatusRef.current === key) return;
    autoStatusRef.current = key;
    const next = getAutoStatusIfValid(job, jobInvoices);
    if (next && next !== job.status) {
      updateMutation.mutate(
        { id: job.id, status: next },
        { onSuccess: () => {
          syncQuoteToJobStatus({ jobId: job.id, newJobStatus: next, allQuotes, updateQuote: updateQuoteMut.mutateAsync }).catch(() => {});
          onRefresh?.();
        } }
      );
    }
  }, [job, jobInvoices, invoicesLoading]);
  const { data: jobTimeEntries = [] } = useTimeEntriesByJob(job?.id);
  const deleteTimeEntry = useDeleteTimeEntry();
  const { data: jobExpenses = [] } = useExpensesByJob(job?.id);
  const deleteExpense = useDeleteExpense();
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [showExpenseForm, setShowExpenseForm] = useState(false);

  // ── Time-logging identity: admins see everyone; hourly crew log their own ──
  const isTimeAdmin = hasAccess('time', 'full', settings);
  const myRosterName = useMemo(
    () => roster.find((r) => r.profileId === profile?.id)?.name || profile?.full_name || '',
    [roster, profile?.id, profile?.full_name]
  );
  // Am I an hourly member of this job's pay split (any dept / any pay mode)?
  const myHourly = useMemo(() => {
    const out = { isHourly: false, estHours: 0 };
    if (!job?.calcSnapshot || !myRosterName) return out;
    const ns = normalizeSnapshot(job.calcSnapshot);
    const me = myRosterName.toLowerCase();
    ['prod', 'post'].forEach((d) => {
      (ns[d]?.hourly || []).forEach((h) => {
        if ((h.name || '').toLowerCase() === me) {
          out.isHourly = true;
          out.estHours += parseFloat(h.estHours) || 0;
        }
      });
    });
    return out;
  }, [job?.calcSnapshot, myRosterName]);
  const visibleTimeEntries = useMemo(
    () => (isTimeAdmin
      ? jobTimeEntries
      : jobTimeEntries.filter((e) => (e.person || '').toLowerCase() === myRosterName.toLowerCase())),
    [jobTimeEntries, isTimeAdmin, myRosterName]
  );
  const canLogTime = isTimeAdmin || myHourly.isHourly || visibleTimeEntries.length > 0;

  if (!job) return <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Select a job to view details</Typography></Paper>;

  const allTransitions = ALL_STATUSES.filter((s) => s !== job.status);
  const forwardTransitions = allTransitions.filter((s) => s !== 'void');

  const handleStatusChange = async (newStatus) => {
    // Backslide guard: if the new status crosses any trigger boundary backward,
    // fetch a preview of protected/forfeited earnings and show a confirmation modal.
    if (isBackslide(job.status, newStatus)) {
      try {
        const preview = await api.get(`/jobs/${job.id}/backslide-preview?to=${encodeURIComponent(newStatus)}`);
        if ((preview?.preserved?.length || 0) + (preview?.forfeited?.length || 0) > 0) {
          setBackslidePending({ newStatus, preview });
          return;
        }
      } catch (err) {
        console.error('[JobDetail] backslide preview failed', err);
        // Fall through and just attempt the update anyway
      }
    }
    await applyStatusChange(newStatus);
  };

  const applyStatusChange = async (newStatus) => {
    try {
      // Server handles timestamp auto-sets (paid_at, voided_at) and transition validation
      const payload = { id: job.id, status: newStatus };
      // Moving back before confirmation clears the confirmed-via stamp
      if (['intake', 'quoted'].includes(newStatus) && (job.confirmedAt || job.confirmedMethod)) {
        payload.confirmedAt = null;
        payload.confirmedMethod = null;
      }
      await updateMutation.mutateAsync(payload);
      // Sync linked quote status
      try { await syncQuoteToJobStatus({ jobId: job.id, newJobStatus: newStatus, allQuotes, updateQuote: updateQuoteMut.mutateAsync }); } catch { /* non-critical */ }
      toast(`Job moved to ${capitalize(newStatus)}`, 'success');
      setBackslidePending(null);
      onRefresh?.();
    } catch (err) { toast(`Failed to update status: ${err.message}`, 'error'); }
  };

  // Send (or resend) an invoice — syncs to Xero, pushes branded PDF as online
  // attachment so the payment link serves our PDF, opens mailto, records sentAt.
  const handleSendInvoice = async (inv) => {
    if (!inv) return;
    const senderName = profile?.full_name || '';
    const templateId = inv.invoiceType === 'deposit'
      ? 'deposit_invoice_sent'
      : inv.invoiceType === 'balance'
        ? 'balance_invoice_sent'
        : 'invoice_sent';

    // 1. Ensure Xero sync so {{paymentLink}} resolves + we have xeroInvoiceId
    let syncedInv = inv;
    let syncFailed = false;
    if (xeroConnected && !inv.xeroOnlineUrl) {
      try {
        const result = await xeroSyncMutation.mutateAsync(inv.id);
        syncedInv = {
          ...inv,
          xeroInvoiceId: result?.xeroInvoiceId || inv.xeroInvoiceId,
          xeroOnlineUrl: result?.xeroOnlineUrl || inv.xeroOnlineUrl,
        };
      } catch (err) {
        syncFailed = true;
        toast(`Xero sync failed — this invoice has no payment link yet: ${err.message}`, 'error');
      }
    }

    // 2. Push branded PDF to Xero as IncludeOnline attachment (best-effort)
    if (xeroConnected && syncedInv.xeroInvoiceId) {
      try {
        await pushInvoicePdfToXero({
          invoice: syncedInv,
          job,
          settings,
          uploadMutateAsync: uploadXeroPdfMutation.mutateAsync,
        });
      } catch (err) {
        toast(`Couldn't upload PDF to Xero: ${err.message}`, 'warning');
      }
    }

    // 3. Open mailto. An invoice pinned to another contact (issued before the
    // job's client was changed) goes to that contact only, never the job's.
    const pinnedElsewhere = !!inv.contactId && inv.contactId !== job.contactId;
    const invContact = pinnedElsewhere ? allContacts.find((c) => c.id === inv.contactId) : null;
    openEmail(templateId, {
      toEmail: pinnedElsewhere ? (invContact?.email || '') : (job.contactEmail || ''),
      invoice: syncedInv,
      job,
      settings,
      senderName,
      allInvoices: jobInvoices,
    });

    // 4. Record sentAt (first send only).
    // NOT when the Xero push failed: the email that just opened carries no
    // payment link, so it is not a real send. Stamping it anyway is how an
    // invoice comes to read "sent" in the portal while the client has no way
    // to pay — and, because the chip then looks normal, nobody notices.
    if (!inv.sentAt && !syncFailed) {
      updateInvoiceMutation.mutateAsync({ id: inv.id, sentAt: new Date().toISOString() })
        .then(() => onRefresh?.())
        .catch((err) => toast(`Failed to record sent: ${err.message}`, 'error'));
    }
  };

  const handleVoid = async () => {
    try {
      await updateMutation.mutateAsync({ id: job.id, status: 'void', voidReason });
      // Sync linked quote → rejected
      try { await syncQuoteToJobStatus({ jobId: job.id, newJobStatus: 'void', allQuotes, updateQuote: updateQuoteMut.mutateAsync }); } catch { /* non-critical */ }
      toast('Job voided', 'success');
      // Notify crew via mailto if checked
      if (voidNotifyCrew && roster.length > 0) {
        const crewEmails = roster.map((m) => m.email).filter(Boolean).join(',');
        if (crewEmails) {
          const subject = encodeURIComponent(`Job Voided: ${job.title || 'Untitled'}`);
          const body = encodeURIComponent(`The following job has been voided:\n\nJob: ${job.title || '(Untitled)'}\nClient: ${job.clientName || 'N/A'}\nReason: ${voidReason || 'Not specified'}\n\n— Sent from RFOS`);
          window.open(`mailto:${crewEmails}?subject=${subject}&body=${body}`, '_self');
        }
      }
      setShowVoidReason(false); setVoidReason(''); setVoidNotifyCrew(false); onRefresh?.();
    } catch (err) { toast(`Failed to void job: ${err.message}`, 'error'); }
  };

  // Admins (jobs:full) delete at any status. all_edit sees the button on
  // early-stage jobs only; whether they are or were in the pay split is the
  // server's call (the job list is already scoped to created/assigned/split
  // jobs), and its 403 sentence lands in the toast.
  const canDelete = hasAction('void_jobs', settings)
    || (hasAction('delete_own_jobs', settings) && ['intake', 'quoted', 'confirmed'].includes(job.status));

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(job.id);
      toast('Job moved to Trash', 'success'); setShowDeleteConfirm(false); onClose?.();
    } catch (err) { toast(`Failed to delete job: ${err.message}`, 'error'); }
  };

  // The server refuses these for everyone (Xero holds its own copy; a bulk
  // invoice's job_ids has no FK), so say so before the click, not after.
  const deleteBlockedBy = jobInvoices.some((i) => i.xeroInvoiceId)
    ? 'an invoice on it is synced to Xero'
    : coveringBulkInvoices.length > 0 ? 'a bulk invoice covers it' : null;
  const deleteBlocked = deleteBlockedBy
    && `This job can't be deleted because ${deleteBlockedBy}. ${hasAction('void_jobs', settings) ? 'Void it instead.' : 'Ask an admin to void it.'}`;

  const deleteDialog = (
    <DeleteJobDialog
      blocked={deleteBlocked}
      open={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={handleDelete}
      pending={deleteMutation.isPending}
      invoices={jobInvoices.length}
      timeEntries={isTimeAdmin ? jobTimeEntries.length : null}
      expenses={hasAccess('expenses', 'full', settings) ? jobExpenses.length : null}
    />
  );

  /* ═══ CONDENSED VIEW — Quick-add jobs in intake ═══ */
  if (job.source === 'quick_add' && job.status === 'intake') {
    const rev = parseFloat(job.revenue) || 0;
    const missing = [];
    if (!job.contactEmail) missing.push('Client email');
    if (!rev) missing.push('Revenue');
    if (!job.shootDate) missing.push('Shoot date');
    if (!job.location) missing.push('Location');

    const JOB_TYPES = ['Real Estate Video', 'FPV Tour', 'Matterport', 'Luxury Film', 'Event', 'VirTour', 'Other'];

    const qfContactResults = quickEdit && qfContactSearch.length >= 1
      ? allContacts.filter((c) => (c.name || '').toLowerCase().includes(qfContactSearch.toLowerCase()) || (c.company || '').toLowerCase().includes(qfContactSearch.toLowerCase()) || (c.email || '').toLowerCase().includes(qfContactSearch.toLowerCase())).slice(0, 6)
      : [];

    const handleQfSelectContact = (contact) => {
      setQfContactId(contact.id);
      setQf((p) => ({ ...p, clientName: contact.name || '', contactEmail: contact.email || '', contactPhone: contact.phone || '' }));
      setQfContactSearch(''); setQfShowDropdown(false);
    };

    /**
     * @param {object} payload  the job patch to send
     * @param {boolean} [override] re-send with the identity guard overridden,
     *   for "this is the same client" (e.g. merging duplicate CRM contacts).
     */
    const submitQuickSave = async (payload, override = false) => {
      try {
        await updateMutation.mutateAsync(
          override
            ? { ...payload, allowIdentityChange: true, identityChangeReason: 'Client change confirmed from Job Detail' }
            : payload,
        );
        toast('Job updated', 'success');
        setIdentityBlock(null);
        setQuickEdit(false);
        onRefresh?.();
      } catch (err) {
        // This edit would change who the job belongs to, and the job has
        // commitments. Explain it rather than surfacing the raw sentence — and
        // keep the payload so the override can re-send it unchanged.
        if (err?.code === 'job_identity_locked') setIdentityBlock({ block: err.body, payload });
        else toast(`Error: ${err.message}`, 'error');
      }
    };

    const handleQuickSave = async () => {
      if (!qf.clientName.trim()) { toast('Client name is required', 'error'); return; }
      if (qf.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(qf.contactEmail)) { toast('Invalid email format', 'error'); return; }
      const loc = qf.location || '';
      const jt = qf.jobType || job.jobType || '';
      const title = loc ? `${jt}: ${loc.split(',')[0].trim()}` : `${jt}: ${qf.clientName.trim()}`;
      await submitQuickSave({
        id: job.id,
        title,
        clientName: qf.clientName.trim(),
        contactName: qf.clientName.trim(),
        contactEmail: qf.contactEmail.trim(),
        contactPhone: qf.contactPhone.trim(),
        contactId: qfContactId || job.contactId || null,
        jobType: qf.jobType || job.jobType,
        workflowType: qf.workflowType || job.workflowType || 'standard',
        shootDate: qf.shootDate || null,
        shootDates: qf.shootDate ? [qf.shootDate] : (job.shootDates || []),
        revenue: qf.revenue || '',
        location: loc,
      });
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Paper sx={{ p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexShrink: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              <Link component="button" variant="caption" color="text.secondary" underline="hover" onClick={() => onClose?.()} sx={{ cursor: 'pointer' }}>Jobs</Link>
              {' / '}{job.title || '(Untitled)'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip label={formatStatus(job.status)} size="small" sx={{ height: 20, fontSize: '0.6rem', fontFamily: '"DM Mono", monospace', fontWeight: 600, textTransform: 'uppercase', bgcolor: `${STATUS_COLORS[job.status] || '#666'}20`, color: STATUS_COLORS[job.status] || '#666', border: `1px solid ${STATUS_COLORS[job.status] || '#666'}40` }} />
              <Typography variant="h6" noWrap sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 0.5 }}>{job.title || '(Untitled)'}</Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.5}>
            {!quickEdit && <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={startQuickEdit} sx={{ fontSize: '0.7rem' }}>Edit</Button>}
            {canDelete && (
              <Button size="small" variant="outlined" color="error" onClick={() => setShowDeleteConfirm(true)} sx={{ fontSize: '0.7rem', minWidth: 0, px: 1 }}><DeleteIcon sx={{ fontSize: 16 }} /></Button>
            )}
          </Stack>
        </Paper>

        {/* Client change refused — explain, and offer the override if allowed */}
        {identityBlock && (
          <JobIdentityBlockDialog
            block={identityBlock.block}
            saving={updateMutation.isPending}
            onOverride={() => submitQuickSave(identityBlock.payload, true)}
            onCancel={() => setIdentityBlock(null)}
          />
        )}

        {/* Delete dialog (shared with the full view) */}
        {deleteDialog}

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, pb: 4, mt: 1 }}>
          {/* TAKE ACTION banner */}
          {!quickEdit && (
            <Paper sx={{ p: 2.5, bgcolor: 'rgba(164,123,26,0.08)', border: '2px solid', borderColor: 'warning.main', borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: missing.length > 0 ? 1.5 : 1 }}>
                <Typography sx={{ fontSize: '1.3rem', lineHeight: 1 }}>⚡</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'warning.dark', fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1, fontSize: '1.15rem' }}>TAKE ACTION</Typography>
              </Stack>
              {missing.length > 0 ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {missing.length} {missing.length === 1 ? 'detail' : 'details'} still needed before quoting
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {missing.map((item) => (
                      <Chip key={item} label={item} size="small" sx={{ height: 22, fontSize: '0.65rem', fontFamily: '"DM Mono", monospace', bgcolor: 'rgba(164,123,26,0.12)', color: 'warning.dark', border: '1px solid rgba(164,123,26,0.30)' }} />
                    ))}
                  </Stack>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, mb: 1.5 }}>All details filled — ready to quote</Typography>
              )}
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" color="warning" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={startQuickEdit} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Edit Details</Button>
                {rev > 0 && (
                  <Button variant="contained" color="primary" onClick={() => navigate(`/quotes?jobId=${job.id}`)} sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}>Create Quote →</Button>
                )}
              </Stack>
            </Paper>
          )}

          {/* Client Details card — read or inline-edit mode */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5, display: 'block', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>
              {quickEdit ? 'Edit Client & Job Details' : 'Job Details'}
            </Typography>

            {quickEdit ? (
              /* ── INLINE EDIT MODE ── */
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {/* Client name with contact autocomplete */}
                <Box sx={{ gridColumn: '1 / -1', position: 'relative' }}>
                  <TextField
                    fullWidth size="small"
                    label={qfContactId ? 'Client (Linked ✓)' : 'Client *'}
                    value={qf.clientName}
                    onChange={(e) => {
                      setQf((p) => ({ ...p, clientName: e.target.value }));
                      setQfContactSearch(e.target.value);
                      setQfShowDropdown(true);
                      if (qfContactId) { setQfContactId(null); }
                    }}
                    onFocus={() => { if (qf.clientName) { setQfContactSearch(qf.clientName); setQfShowDropdown(true); } }}
                    onBlur={() => setTimeout(() => setQfShowDropdown(false), 200)}
                    placeholder="Type to search contacts..."
                  />
                  {qfShowDropdown && qfContactResults.length > 0 && (
                    <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, maxHeight: 200, overflowY: 'auto' }}>
                      <List dense sx={{ py: 0 }}>
                        {qfContactResults.map((c) => (
                          <ListItemButton key={c.id} data-track="job.contactPick" onMouseDown={() => handleQfSelectContact(c)} sx={{ py: 0.5 }}>
                            <ListItemText primary={c.name} secondary={[c.company, c.email].filter(Boolean).join(' · ')} primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.7rem' }} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
                <Box>
                  <TextField fullWidth size="small" label="Email" value={qf.contactEmail} onChange={(e) => setQf((p) => ({ ...p, contactEmail: e.target.value }))} type="email" />
                </Box>
                <Box>
                  <TextField fullWidth size="small" label="Phone" value={qf.contactPhone} onChange={(e) => setQf((p) => ({ ...p, contactPhone: e.target.value }))} type="tel" />
                </Box>
                <Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select value={qf.jobType} onChange={(e) => setQf((p) => ({ ...p, jobType: e.target.value }))} label="Type">
                      {JOB_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={qf.workflowType === 'staycation'}
                        onChange={(e) => setQf((p) => ({ ...p, workflowType: e.target.checked ? 'staycation' : 'standard' }))}
                      />
                    }
                    label="Staycation workflow"
                  />
                </Box>
                <Box>
                  <TextField fullWidth size="small" label="Shoot Date" type="date" value={qf.shootDate} onChange={(e) => setQf((p) => ({ ...p, shootDate: e.target.value }))} slotProps={{ inputLabel: { shrink: true } }} />
                </Box>
                <Box>
                  <TextField fullWidth size="small" label="Revenue" type="number" value={qf.revenue} onChange={(e) => setQf((p) => ({ ...p, revenue: e.target.value }))} slotProps={{ input: { inputProps: { step: '0.01', min: '0' } } }} />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <AddressAutocomplete variant="mui" label="Location" value={qf.location} onChange={(v) => setQf((p) => ({ ...p, location: v }))} />
                </Box>
                {/* Save / Cancel */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Button variant="outlined" size="small" onClick={cancelQuickEdit}>Cancel</Button>
                    <Button variant="contained" size="small" onClick={handleQuickSave} disabled={updateMutation.isPending}>{updateMutation.isPending ? 'Saving...' : 'Save'}</Button>
                  </Stack>
                </Box>
              </Box>
            ) : (
              /* ── READ MODE ── */
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Client</Typography>
                  {job.contactId ? (
                    <Button size="small" data-track="job.client" onClick={() => navigateToContact(navigate, job.contactId)} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontWeight: 600 }}>{job.clientName || 'N/A'} →</Button>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.clientName || 'N/A'}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  {job.contactEmail ? (
                    <Link href={`mailto:${job.contactEmail}`} variant="body2" sx={{ display: 'block', wordBreak: 'break-all', fontWeight: 600 }}>{job.contactEmail}</Link>
                  ) : <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>—</Typography>}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.contactPhone || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.jobType || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Shoot Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: job.shootDate ? 'text.primary' : 'warning.main' }}>{fmtD(job.shootDate) || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Revenue</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem', color: rev > 0 ? 'text.primary' : 'warning.main' }}>{rev > 0 ? fmtC(rev) : '—'}</Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  {job.location ? (
                    <Link href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location)}`} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {job.location} <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </Link>
                  ) : <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>—</Typography>}
                </Box>
              </Box>
            )}
            {!quickEdit && job.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, whiteSpace: 'pre-wrap' }}><Typography variant="body2">{job.notes}</Typography></Paper>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    );
  }

  const activeInvoices = jobInvoices.filter((inv) => inv.status !== 'void');
  const totalInvoiced = activeInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);
  const totalPaid = activeInvoices.reduce((sum, inv) => sum + (parseFloat(inv.paidAmount) || 0), 0);

  return (
    <Box data-track-area="job-detail" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ═══ HEADER ═══ */}
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexShrink: 0, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px', minWidth: { xs: 0, sm: 300 } }}>
          {/* Back to Jobs */}
          <Link component="button" variant="caption" color="text.secondary" underline="hover" onClick={() => onClose?.()} sx={{ display: 'inline-block', mb: 0.5, cursor: 'pointer' }}>‹ Jobs</Link>
          {/* Title */}
          <Typography variant="h6" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 0.5, lineHeight: 1.15 }}>{job.title || '(Untitled)'}</Typography>
          {/* Status + type chips */}
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <Chip
              label={formatStatus(job.status)}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                fontFamily: '"DM Mono", monospace',
                fontWeight: 600,
                textTransform: 'uppercase',
                bgcolor: `${STATUS_COLORS[job.status] || '#666'}20`,
                color: STATUS_COLORS[job.status] || '#666',
                border: `1px solid ${STATUS_COLORS[job.status] || '#666'}40`,
              }}
            />
            {job.status === 'post_production' && (() => {
              const nextShoot = getNextShootDate(job);
              return nextShoot ? (
                <Chip label={`📅 Next shoot: ${fmtD(nextShoot)}`} size="small" sx={{ height: 18, fontSize: '0.55rem', fontFamily: '"DM Mono", monospace', bgcolor: '#2E7D9B18', color: '#2E7D9B', border: '1px solid #2E7D9B40' }} />
              ) : null;
            })()}
            {job.jobType && (
              <Chip
                label={job.jobType === 'Real Estate' ? 'RE' : job.jobType === 'Custom' ? 'CU' : job.jobType}
                size="small"
                sx={{ height: 18, fontSize: '0.55rem', fontFamily: '"DM Mono", monospace', fontWeight: 700, bgcolor: job.jobType === 'Real Estate' ? '#1a7fa318' : '#7049a818', color: job.jobType === 'Real Estate' ? '#1a7fa3' : '#7049a8' }}
              />
            )}
          </Stack>
          {job.contactId ? (
            <Button size="small" data-track="job.client" onClick={() => navigateToContact(navigate, job.contactId)} sx={{ textTransform: 'none', p: 0, minWidth: 0, mt: 0.5 }}>{job.clientName || 'N/A'} →</Button>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{job.clientName || 'N/A'}</Typography>
          )}
        </Box>
        <Stack direction="row" spacing={isMobile ? 0.5 : 1} flexWrap="wrap" justifyContent="flex-end" useFlexGap sx={{ flexShrink: 0 }}>
          {allTransitions.length > 0 && (
            <Select
              value=""
              displayEmpty
              size="small"
              disabled={updateMutation.isPending}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) return;
                if (val === 'void') { setShowVoidReason(true); return; }
                handleStatusChange(val);
              }}
              renderValue={() => <Typography variant="caption" sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.65rem', color: 'text.secondary' }}>Set Status</Typography>}
              sx={{ minWidth: isMobile ? 90 : 130, height: 28, '.MuiSelect-select': { py: 0.5, px: 1 } }}
            >
              {forwardTransitions.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: '0.8rem' }}>{TRANSITION_LABELS[s] || formatStatus(s)}</MenuItem>
              ))}
              {job.status !== 'void' && hasAction('void_jobs', settings) && <Divider />}
              {job.status !== 'void' && hasAction('void_jobs', settings) && (
                <MenuItem value="void" sx={{ fontSize: '0.8rem', color: 'error.main' }}>⛔ Void Job</MenuItem>
              )}
            </Select>
          )}
          <Button size="small" variant="outlined" startIcon={!isMobile ? <EmailIcon /> : undefined} onClick={() => { if (job.contactEmail) window.location.href = `mailto:${encodeURIComponent(job.contactEmail)}`; }} disabled={!job.contactEmail} sx={{ fontSize: '0.7rem', ...(isMobile && { minWidth: 0, px: 1 }) }}>{isMobile ? <EmailIcon sx={{ fontSize: 16 }} /> : 'Email Client'}</Button>
          {canLogTime && (visibleTimeEntries.length > 0 || !isHidden('job.logTime')) && (
            <Badge data-focus="job.logTime" badgeContent={visibleTimeEntries.length || null} color="primary" max={99} sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 16, minWidth: 16 } }}>
              <Button size="small" variant="outlined" startIcon={!isMobile ? <AccessTimeIcon sx={{ fontSize: 16 }} /> : undefined} onClick={() => setShowTimeDialog(true)} sx={{ fontSize: '0.7rem', ...(isMobile && { minWidth: 0, px: 1 }) }}>{isMobile ? <AccessTimeIcon sx={{ fontSize: 16 }} /> : (isTimeAdmin ? 'Log Time' : 'My Hours')}</Button>
            </Badge>
          )}
          {(jobExpenses.length > 0 || !isHidden('job.logExpenses')) && (
            <Badge data-focus="job.logExpenses" badgeContent={jobExpenses.length || null} color="primary" max={99} sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 16, minWidth: 16 } }}>
              <Button size="small" variant="outlined" startIcon={!isMobile ? <AttachMoneyIcon sx={{ fontSize: 16 }} /> : undefined} onClick={() => setShowExpenseDialog(true)} sx={{ fontSize: '0.7rem', ...(isMobile && { minWidth: 0, px: 1 }) }}>{isMobile ? <AttachMoneyIcon sx={{ fontSize: 16 }} /> : 'Log Expenses'}</Button>
            </Badge>
          )}
          <Button size="small" variant="outlined" startIcon={!isMobile ? <EditIcon /> : undefined} onClick={() => navigate(`/quotes?jobId=${job.id}`)} sx={{ fontSize: '0.7rem', ...(isMobile && { minWidth: 0, px: 1 }) }}>{isMobile ? <EditIcon sx={{ fontSize: 16 }} /> : 'Edit'}</Button>
          {canDelete && (
            <Button size="small" variant="outlined" color="error" onClick={() => setShowDeleteConfirm(true)} sx={{ fontSize: '0.7rem', minWidth: 0, px: 1 }}><DeleteIcon sx={{ fontSize: 16 }} /></Button>
          )}
        </Stack>
      </Paper>

      {/* ═══ DIALOGS (float above scroll) ═══ */}
      {backslidePending && (
        <StatusBackslideModal
          preview={backslidePending.preview}
          fromStatus={job.status}
          toStatus={backslidePending.newStatus}
          onCancel={() => setBackslidePending(null)}
          onConfirm={() => applyStatusChange(backslidePending.newStatus)}
          isLoading={updateMutation.isPending}
        />
      )}
      <Dialog open={showVoidReason} onClose={() => { setShowVoidReason(false); setVoidReason(''); setVoidNotifyCrew(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Void Job</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Reason (required)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g., Client cancelled..." multiline rows={2} sx={{ mt: 1 }} inputProps={{ maxLength: 500 }} />
          <FormControlLabel
            control={<Checkbox size="small" checked={voidNotifyCrew} onChange={(e) => setVoidNotifyCrew(e.target.checked)} />}
            label={<Typography variant="caption" color="text.secondary">Notify crew ({roster.length} members)</Typography>}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowVoidReason(false); setVoidReason(''); setVoidNotifyCrew(false); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleVoid} disabled={updateMutation.isPending || !voidReason.trim()}>Confirm Void</Button>
        </DialogActions>
      </Dialog>

      {deleteDialog}

      {/* ═══ SCROLLABLE BODY — V1-style single flow ═══ */}
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>

        {/* ── Void Banner ── */}
        {(job.status === 'void' || job.archivedFromVoid) && (
          <Alert severity="error" variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>⛔ VOIDED</Typography>
            {job.voidReason && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Reason: {job.voidReason}</Typography>}
            {job.voidedAt && <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>Voided {fmtD(job.voidedAt)}</Typography>}
          </Alert>
        )}

        {/* ── Quick-Add Completeness Banner ── */}
        {job.source === 'quick_add' && job.status === 'intake' && (() => {
          const missing = [];
          if (!job.contactEmail) missing.push('Client email');
          if (!(parseFloat(job.revenue) > 0)) missing.push('Revenue');
          if (!job.shootDate) missing.push('Shoot date');
          if (!job.location) missing.push('Location');
          if (!job.calcSnapshot?.quoteNumber) missing.push('Quote');
          if (missing.length === 0) return null;
          return (
            <Paper variant="outlined" sx={{ p: 2, borderColor: 'warning.main', borderStyle: 'dashed', bgcolor: 'rgba(164,123,26,0.04)' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  ⚡ Quick-add job — {missing.length} {missing.length === 1 ? 'item needs' : 'items need'} attention
                </Typography>
                <Button size="small" variant="outlined" color="warning" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => navigate(`/quotes?jobId=${job.id}`)} sx={{ textTransform: 'none', fontSize: '0.7rem', height: 26 }}>
                  Edit Job
                </Button>
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {missing.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    size="small"
                    sx={{ height: 22, fontSize: '0.65rem', fontFamily: '"DM Mono", monospace', bgcolor: 'rgba(164,123,26,0.10)', color: 'warning.dark', border: '1px solid rgba(164,123,26,0.25)' }}
                  />
                ))}
              </Stack>
              {!job.calcSnapshot?.quoteNumber && parseFloat(job.revenue) > 0 && (
                <Button size="small" variant="text" color="warning" onClick={() => navigate(`/quotes?jobId=${job.id}`)} sx={{ textTransform: 'none', fontSize: '0.7rem', mt: 1, p: 0 }}>
                  Create Quote →
                </Button>
              )}
            </Paper>
          );
        })()}

        {/* ── Calculator Split Block ── */}
        {(() => {
          const ns = normalizeSnapshot(job.calcSnapshot);
          const hasSnap = !!(ns.savedAt || ns.originators.length > 0 || ns.prod.lead || ns.post.lead);
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          const shootPast = job.shootDate && job.shootDate.split('T')[0] <= todayStr;
          const isActive = ['confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced'].includes(job.status);
          const needsSplit = shootPast && !hasSnap && isActive;

          if (needsSplit) {
            return (
              <Box sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', borderRadius: 2, p: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>⚡ Action Required</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>Shoot complete — run the pay split</Typography>
                {job.revenue > 0 && <Typography variant="caption" sx={{ fontFamily: 'monospace', opacity: 0.8 }}>{fmtC(job.revenue)} will pre-fill</Typography>}
                <Box sx={{ mt: 1 }}>
                  <Button size="small" variant="contained" color="inherit" onClick={() => navigate(`/calculator?jobId=${job.id}`)} sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'warning.main', bgcolor: 'warning.contrastText' }}>⚡ Run Split</Button>
                </Box>
              </Box>
            );
          }
          return null; // Snapshot shown later in its own section
        })()}

        {/* ── Pipeline (always visible) ── */}
        <JobPipeline job={job} settings={settings} invoices={jobInvoices} roster={roster} onCreateInvoice={(type) => { setInvoiceFormType(type || null); setShowInvoiceForm(true); }} onStatusChange={(s) => handleStatusChange(s)} onUpdateJob={(updates) => updateMutation.mutateAsync(updates).then(() => onRefresh?.())} onRecordPayment={async (invoiceId, payload) => {
          // payload = { amount, method, note }
          const inv = jobInvoices.find((i) => i.id === invoiceId);
          if (!inv) { toast('Invoice not found', 'error'); return; }
          const amount = parseFloat(payload?.amount) || 0;
          const method = payload?.method || null;
          const note = payload?.note || '';
          const prevPaid = parseFloat(inv.paidAmount) || 0;
          const invAmt = parseFloat(inv.amount) || 0;
          const nextPaid = Math.round((prevPaid + amount) * 100) / 100;
          const fullyPaid = nextPaid >= invAmt;
          const nowIso = new Date().toISOString();
          // `date` is what every reader keys off (see paymentDate in lib/utils);
          // `at` stays as the full audit timestamp. Writing only `at` hid
          // $6,322.50 of collected cash from Financials (audit V-02).
          const entry = { date: nowIso.slice(0, 10), at: nowIso, amount, method, note, recordedBy: profile?.id || null };
          const nextPayments = [...(Array.isArray(inv.payments) ? inv.payments : []), entry];
          const updates = {
            id: invoiceId,
            status: fullyPaid ? 'paid' : 'partial',
            paidAmount: nextPaid,
            paymentMethod: method,
            paymentNote: note || null,
            payments: nextPayments,
          };
          if (fullyPaid) updates.paidAt = nowIso;
          try {
            await updateInvoiceMutation.mutateAsync(updates);
            toast(fullyPaid ? 'Payment recorded — fully paid' : `Payment recorded (${fmtC(amount)})`, 'success');
            onRefresh?.();
            if (fullyPaid && xeroConnected) xeroSyncMutation.mutate(invoiceId);
          } catch (err) {
            toast(`Failed: ${err.message}`, 'error');
            throw err;
          }
        }} onSendInvoice={handleSendInvoice} />

        {/* ── Project Details ── */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5, display: 'block', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>Project Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.jobType || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Location</Typography>
              {job.location ? (
                <Box>
                  <Link
                    href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(homebase)}&destination=${encodeURIComponent(job.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {job.location} <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Link>
                  {travelMins != null && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <DirectionsCarIcon sx={{ fontSize: 11 }} /> ~{travelMins} min from base
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>N/A</Typography>
              )}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Contact</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.contactName || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              {job.contactEmail ? (
                <Link href={`mailto:${job.contactEmail}`} variant="body2" sx={{ display: 'block', wordBreak: 'break-all', fontWeight: 600 }}>{job.contactEmail}</Link>
              ) : <Typography variant="body2" sx={{ fontWeight: 600 }}>—</Typography>}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Phone</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.contactPhone || '—'}</Typography>
            </Box>
            {(() => {
              const ns = normalizeSnapshot(job.calcSnapshot);
              const dlLines = ns.deliverableLines || [];
              const dates = dlLines.map((dl) => dl.shootDate).filter(Boolean);
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              if (dates.length === 0) {
                // Fallback to top-level shoot fields
                const multi = Array.isArray(job.shootDates) && job.shootDates.length > 1;
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Shoot Date{multi ? 's' : ''}</Typography>
                    {multi ? (
                      <Stack spacing={0.25}>
                        {job.shootDates.map((d, i) => (
                          <Typography key={d} variant="body2" sx={{ fontWeight: 600 }}>Day {i + 1}: {fmtD(d)}</Typography>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtD(job.shootDate) || 'N/A'}</Typography>
                    )}
                  </Box>
                );
              }
              const unique = [...new Set(dates)];
              if (unique.length === 1) {
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Shoot Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtD(unique[0])}</Typography>
                  </Box>
                );
              }
              const futureDates = dates.filter((d) => new Date(d) >= now).sort();
              const completed = dates.filter((d) => new Date(d) < now).length;
              if (futureDates.length > 0) {
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Next Shoot</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtD(futureDates[0])}</Typography>
                    <Typography variant="caption" color="text.disabled">({completed} of {dates.length} complete)</Typography>
                  </Box>
                );
              }
              return (
                <Box>
                  <Typography variant="caption" color="text.secondary">Last Shoot</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtD(dates.sort().pop())}</Typography>
                </Box>
              );
            })()}
            {/* GCal sync indicator */}
            {gcalEnabled && job.shootDate && (
              <Box>
                <Typography variant="caption" color="text.secondary">Calendar</Typography>
                {gcalStatus?.synced ? (
                  <Chip
                    label={`📅 Synced`}
                    size="small"
                    onClick={() => syncToGcalMut.mutate({ jobId: job.id, silent: true }, {
                      onSuccess: () => toast('Calendar re-synced', 'success'),
                      onError: (e) => toast(`Sync failed: ${e.message}`, 'error'),
                    })}
                    sx={{ height: 20, fontSize: '0.65rem', fontFamily: '"DM Mono", monospace', bgcolor: '#1e8b4a18', color: '#1e8b4a', border: '1px solid #1e8b4a40', cursor: 'pointer', '&:hover': { bgcolor: '#1e8b4a28' } }}
                  />
                ) : (
                  <Chip
                    label="📅 Not synced"
                    size="small"
                    onClick={() => syncToGcalMut.mutate({ jobId: job.id }, {
                      onSuccess: (d) => toast(`Synced ${d.created || 0} event(s)`, 'success'),
                      onError: (e) => toast(`Sync failed: ${e.message}`, 'error'),
                    })}
                    sx={{ height: 20, fontSize: '0.65rem', fontFamily: '"DM Mono", monospace', bgcolor: 'action.hover', color: 'text.secondary', border: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                  />
                )}
              </Box>
            )}
            {(() => {
              const ns = normalizeSnapshot(job.calcSnapshot);
              const dlLines = ns.deliverableLines || [];
              const dates = dlLines.map((dl) => dl.deliveryDate).filter(Boolean);
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              if (dates.length === 0) {
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Delivery Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtD(job.deliveryDate) || 'N/A'}</Typography>
                  </Box>
                );
              }
              const unique = [...new Set(dates)];
              if (unique.length === 1) {
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Delivery Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtD(unique[0])}</Typography>
                  </Box>
                );
              }
              const futureDates = dates.filter((d) => new Date(d) >= now).sort();
              const delivered = dates.filter((d) => new Date(d) < now).length;
              if (futureDates.length > 0) {
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Next Delivery</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtD(futureDates[0])}</Typography>
                    <Typography variant="caption" color="text.disabled">({delivered} of {dates.length} delivered)</Typography>
                  </Box>
                );
              }
              return (
                <Box>
                  <Typography variant="caption" color="text.secondary">Last Delivery</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtD(dates.sort().pop())}</Typography>
                </Box>
              );
            })()}
            <Box>
              <Typography variant="caption" color="text.secondary">Originator</Typography>
              {job.originator ? (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3, '&:hover': { color: 'primary.main' } }}
                  onClick={() => { const match = allContacts.find((c) => c.name === job.originator); if (match) navigateToContact(navigate, match.id); }}
                >
                  {job.originator}
                </Typography>
              ) : <Typography variant="body2" sx={{ fontWeight: 600 }}>N/A</Typography>}
            </Box>
          </Box>
          {/* Notes */}
          {job.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, whiteSpace: 'pre-wrap' }}><Typography variant="body2">{job.notes}</Typography></Paper>
            </Box>
          )}
        </Paper>

        {/* ── Property Details (RE only) ── */}
        {(() => {
          const snap = normalizeSnapshot(job.calcSnapshot);
          const isRE = job.jobType === 'Real Estate' || job.jobType === 'Real Estate Video' || job.jobType === 'VirTour' || snap.mode === 're' || snap.mode === 'virtour';
          if (!isRE) return null;
          const pType = job.propertyType || snap.propertyType;
          const beds = job.bedrooms || snap.bedrooms;
          const baths = job.bathrooms || snap.bathrooms;
          const sf = job.sqft || snap.sqft;
          const hasData = pType || beds || baths || sf;
          return (
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5, display: 'block', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>Property Details</Typography>
              {hasData ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Property Type</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{pType || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Bedrooms</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{beds || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Bathrooms</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{baths || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Sq Ft</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{sf ? Number(sf).toLocaleString() : '—'}</Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No property details — create a quote to add</Typography>
              )}
            </Paper>
          );
        })()}

        {/* ── Notes ── */}
        <HandoffNotes
          notes={Array.isArray(job.handoffNotes) ? job.handoffNotes : []}
          currentUser={profile?.full_name || 'Unknown'}
          saving={updateMutation.isPending}
          onSave={async (updated) => {
            try {
              await updateMutation.mutateAsync({ id: job.id, handoffNotes: updated });
              toast('Notes updated', 'success');
              onRefresh?.();
              return true;
            } catch (err) {
              toast(`Failed: ${err.message}`, 'error');
              return false;
            }
          }}
        />

        {/* ── Project Timeline ── */}
        {(() => {
          const rows = [];
          // Extract deliverableLines from calc snapshot for times
          const ns = normalizeSnapshot(job.calcSnapshot);
          const dlLines = ns.deliverableLines || [];
          // Build shoot-time map: date → Set of times
          const shootTimesMap = {};
          // Build duration map: date → total minutes
          const shootDurMap = {};
          dlLines.forEach((dl) => {
            if (dl.shootDate && dl.shootTime) {
              if (!shootTimesMap[dl.shootDate]) shootTimesMap[dl.shootDate] = new Set();
              shootTimesMap[dl.shootDate].add(dl.shootTime);
            }
            if (dl.shootDate && dl.dur) {
              shootDurMap[dl.shootDate] = (shootDurMap[dl.shootDate] || 0) + dl.dur;
            }
          });
          // Shoot dates
          const shootDates = Array.isArray(job.shootDates) && job.shootDates.length > 0
            ? job.shootDates : (job.shootDate ? [job.shootDate] : []);
          shootDates.forEach((d, i) => {
            const times = shootTimesMap[d] ? Array.from(shootTimesMap[d]).sort() : [];
            // Compute end time from earliest start + total duration
            let endTime = null;
            if (times.length > 0 && shootDurMap[d]) {
              const [hh, mm] = times[0].split(':').map(Number);
              if (!isNaN(hh) && !isNaN(mm)) {
                const totalMin = hh * 60 + mm + shootDurMap[d];
                const eh = Math.floor(totalMin / 60) % 24;
                const em = totalMin % 60;
                const ampm = eh >= 12 ? 'p' : 'a';
                const h12 = eh === 0 ? 12 : eh > 12 ? eh - 12 : eh;
                endTime = `${h12}:${String(em).padStart(2, '0')}${ampm}`;
              }
            }
            // Compute "leave by" from earliest shoot time minus travel minus 5-min buffer
            let leaveBy = null;
            if (travelMins && times.length > 0) {
              const [hh, mm] = times[0].split(':').map(Number);
              if (!isNaN(hh) && !isNaN(mm)) {
                const totalMin = hh * 60 + mm - travelMins - 5;
                if (totalMin > 0) {
                  const lh = Math.floor(totalMin / 60);
                  const lm = totalMin % 60;
                  const ampm = lh >= 12 ? 'p' : 'a';
                  const h12 = lh === 0 ? 12 : lh > 12 ? lh - 12 : lh;
                  leaveBy = `${h12}:${String(lm).padStart(2, '0')}${ampm}`;
                }
              }
            }
            const timeStr = times.length
              ? times.map(fmtT).join(', ') + (endTime ? ` – ${endTime}` : '')
              : null;
            rows.push({ icon: '📅', label: 'SHOOT', date: d, time: timeStr, suffix: shootDates.length > 1 ? `Day ${i + 1}` : null, dateColor: '#10B981', leaveBy });
          });
          // Deliverables (from deliverableLines if available, fallback to text)
          if (dlLines.length > 0) {
            dlLines.forEach((dl) => {
              rows.push({
                icon: '📦', desc: dl.desc || '', date: dl.deliveryDate || job.deliveryDate,
                time: dl.deliveryTime ? fmtT(dl.deliveryTime) : null,
                shootRef: dl.shootDate ? fmtD(dl.shootDate) + (dl.shootTime ? ' ' + fmtT(dl.shootTime) : '') : null,
                dateColor: '#D2691E', label: null,
              });
            });
          } else if (job.deliverables) {
            const delItems = splitDeliverables(job.deliverables);
            delItems.forEach((desc) => {
              rows.push({ icon: '📦', desc, date: job.deliveryDate, dateColor: '#D2691E', label: null });
            });
          } else if (job.deliveryDate) {
            rows.push({ icon: '📦', label: 'DELIVERY', date: job.deliveryDate, dateColor: '#D2691E' });
          }
          // Paid / Voided
          if (job.paidAt) rows.push({ icon: '✅', label: 'PAID', date: job.paidAt, dateColor: '#10B981' });
          if (job.voidedAt) rows.push({ icon: '⛔', label: 'VOIDED', date: job.voidedAt, dateColor: '#EF4444' });

          return rows.length > 0 ? (
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5, display: 'block', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>Project Timeline</Typography>
              {rows.map((row, idx) => {
                const dateStr = row.date?.split?.('T')?.[0] || '';
                return (
                  <Box
                    key={idx}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, flexWrap: 'wrap', borderBottom: idx < rows.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                  >
                    <Typography sx={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}>{row.icon}</Typography>
                    {row.label && (
                      <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.6rem', color: 'text.disabled', width: 52, flexShrink: 0, textTransform: 'uppercase', fontWeight: 600 }}>{row.label}</Typography>
                    )}
                    {row.desc && (
                      <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                        {row.desc}
                        {row.shootRef && <Typography component="span" sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.55rem', color: 'text.disabled', ml: 0.75 }}>📅 {row.shootRef}</Typography>}
                      </Typography>
                    )}
                    {!row.desc && !row.label && <Box sx={{ flex: 1 }} />}
                    {dateStr && (
                      <Typography
                        component="span"
                        onClick={() => navigate(`/calendar?date=${dateStr}`)}
                        sx={{ fontSize: '0.8rem', color: row.dateColor, cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3, fontWeight: 600, flexShrink: 0, '&:hover': { opacity: 0.7 } }}
                      >
                        {fmtD(row.date)}{row.time ? ` ${row.time}` : ''}
                      </Typography>
                    )}
                    {row.suffix && (
                      <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.55rem', color: 'text.disabled', flexShrink: 0 }}>{row.suffix}</Typography>
                    )}
                    {row.leaveBy && (
                      <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.55rem', color: 'warning.main', flexShrink: 0 }}>🚗 Leave by {row.leaveBy} <Typography component="span" sx={{ fontSize: '0.5rem', color: 'text.disabled' }}>(+5 min buffer)</Typography></Typography>
                    )}
                  </Box>
                );
              })}
            </Paper>
          ) : null;
        })()}

        {/* ── Delivery Links ── */}
        {job.deliveryLinks && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>Delivery Links</Typography>
            <Stack spacing={0.5}>
              {job.deliveryLinks.split(/[\n,]+/).map((link) => link.trim()).filter(Boolean).map((link, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', minWidth: 16 }}>{i + 1}.</Typography>
                  <Link
                    href={link.startsWith('http') ? link : `https://${link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {link} <OpenInNewIcon sx={{ fontSize: 11, flexShrink: 0 }} />
                  </Link>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        {/* ── Financials ── */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5, display: 'block', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>Financials</Typography>
          {/* Revenue summary */}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {job.discount > 0 && (
              <Box>
                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase' }}>Subtotal</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem', opacity: 0.5 }}>{fmtC((parseFloat(job.revenue) || 0) + (parseFloat(job.discount) || 0))}</Typography>
              </Box>
            )}
            {job.discount > 0 && (
              <Box>
                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase' }}>Discount</Typography>
                <Typography variant="body1" color="error" sx={{ fontWeight: 700, fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem' }}>-{fmtC(job.discount)}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase' }}>Revenue</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem' }}>{fmtC(job.revenue)}</Typography>
            </Box>
            {totalInvoiced > 0 && (
              <>
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase' }}>Invoiced</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem', color: '#14B8A6' }}>{fmtC(totalInvoiced)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase' }}>Received</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem', color: '#10B981' }}>{fmtC(totalPaid)}</Typography>
                </Box>
                {(() => {
                  const outstanding = Math.max(0, totalInvoiced - totalPaid);
                  return outstanding > 0 ? (
                    <Box>
                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase' }}>Outstanding</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.1rem', color: '#D97706' }}>{fmtC(outstanding)}</Typography>
                    </Box>
                  ) : null;
                })()}
              </>
            )}
          </Stack>

          {/* Invoice list */}
          {showInvoiceForm ? (
            <Box sx={{ mb: 2 }}>
              <InvoiceForm job={job} prefillType={invoiceFormType} onSave={() => { setShowInvoiceForm(false); setInvoiceFormType(null); onRefresh?.(); }} onCancel={() => { setShowInvoiceForm(false); setInvoiceFormType(null); }} />
            </Box>
          ) : (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                disabled={coveringBulkInvoices.length > 0}
                title={coveringBulkInvoices.length > 0 ? 'Covered by a bulk invoice — void it first to bill this job on its own' : undefined}
                onClick={() => { setInvoiceFormType(null); setShowInvoiceForm(true); }}
                sx={{ textTransform: 'none', fontSize: '0.72rem' }}
              >+ Create</Button>
              {!isHidden('job.financials.extra') && (
                <Button data-focus="job.financials.extra" variant="outlined" size="small" fullWidth onClick={() => { setInvoiceFormType('extra'); setShowInvoiceForm(true); }} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>+ Extra</Button>
              )}
              {!isHidden('job.financials.link') && (
                <Button data-focus="job.financials.link" variant="outlined" size="small" fullWidth onClick={() => setShowLinkModal(true)} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>Link…</Button>
              )}
            </Stack>
          )}

          {coveringBulkInvoices.map((inv) => {
            const li = (inv.lineItems || []).find((l) => l.jobId === job.id);
            return (
              <Box
                key={inv.id}
                onClick={() => navigateToInvoice(navigate, inv.id)}
                sx={{ p: 1.5, mb: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, border: 1, borderColor: 'info.main', borderRadius: 1, bgcolor: 'rgba(19,181,234,0.05)', '&:hover': { borderColor: 'primary.main' } }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                    Covered by bulk invoice {inv.number}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {inv.jobIds.length} jobs · {inv.status} · this job {fmtC(li?.amount)}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1rem', flexShrink: 0 }}>{fmtC(inv.amount)}</Typography>
              </Box>
            );
          })}
          {invoicesLoading ? (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 1, display: 'block' }}>Loading invoices...</Typography>
          ) : jobInvoices.length === 0 && coveringBulkInvoices.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 1, display: 'block' }}>No invoices yet</Typography>
          ) : jobInvoices.length === 0 ? null : (
            <Stack spacing={1}>
              {jobInvoices.map((inv) => {
                const balance = (parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0);
                const statusColor = INVOICE_STATUS_COLORS[inv.status] || '#999';
                return (
                  <Box key={inv.id} onClick={() => navigateToInvoice(navigate, inv.id)} sx={{ p: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, border: 1, borderColor: 'divider', borderRadius: 1, '&:hover': { borderColor: 'primary.main' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{inv.number || 'Draft'}</Typography>
                        <Chip size="small" label={inv.status} sx={{ fontSize: '0.6rem', height: 18, bgcolor: statusColor + '20', border: `1px solid ${statusColor}40`, color: statusColor }} />
                        <Chip size="small" label={inv.invoiceType || 'full'} variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Due {fmtD(inv.dueDate)}</Typography>
                      {inv.paymentMethod && (inv.status === 'paid' || inv.status === 'partial') && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                          Paid via {getPaymentMethodLabel(inv.paymentMethod)}{inv.paymentNote ? ` · ${inv.paymentNote}` : ''}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1rem' }}>{fmtC(inv.amount)}</Typography>
                      {balance > 0 && inv.status !== 'void' && <Typography variant="caption" sx={{ color: statusColor, fontSize: '0.6rem' }}>{fmtC(balance)} due</Typography>}
                      {inv.status === 'paid' && <Typography variant="caption" sx={{ color: '#10B981', fontSize: '0.6rem' }}>Paid in full</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* ── Earnings Ledger (admin/payroll-full) ── */}
        {hasAccess('payroll', 'full') && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5, display: 'block', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>Earnings Ledger</Typography>
            <JobLedgerPanel jobId={job.id} />
          </Paper>
        )}

        {/* ── Calculator Snapshot ── */}
        {(() => {
          const ns = normalizeSnapshot(job.calcSnapshot);
          const hasSnap = !!(ns.savedAt || ns.originators.length > 0 || ns.prod.lead || ns.post.lead);
          if (!hasSnap) {
            // Not-yet state (no action required, just informational)
            return (
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {job.revenue ? `Ready to split — ${fmtC(job.revenue)}` : 'Add revenue to enable split'}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => navigate(`/calculator?jobId=${job.id}`)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Open Calculator</Button>
                </Box>
              </Paper>
            );
          }

          const ROLE_COLORS = { company: '#D2691E', orig: '#2E7D9B', prod: '#10B981', post: '#8B5CF6' };
          const ROLE_LABELS = { company: 'RF', orig: 'Orig', prod: 'Prod', post: 'Post' };
          const roles = ['company', 'orig', 'prod', 'post'];
          const fmtP = (n) => (n || 0).toFixed(1) + '%';

          const personTotals = {};
          const personTags = {};
          const personHasEst = {};
          const personHoursTracking = {}; // { name: { logged, estimated } }
          // Build logged hours map from time entries for this job
          const loggedByPerson = {};
          jobTimeEntries.forEach(e => {
            const n = (e.person || '').toLowerCase();
            loggedByPerson[n] = (loggedByPerson[n] || 0) + (parseFloat(e.hours) || 0);
          });
          const personExpenseDeductions = {};
          ns.originators.forEach((o) => {
            personTotals[o.name] = (personTotals[o.name] || 0) + o.earned;
            if (!personTags[o.name]) personTags[o.name] = [];
            personTags[o.name].push({ label: 'Orig', chipType: 'lead' });
            if (o.expenseDeduction > 0) personExpenseDeductions[o.name] = (personExpenseDeductions[o.name] || 0) + o.expenseDeduction;
          });
          ['prod', 'post'].forEach((dept) => {
            const d = ns[dept]; const deptLabel = dept === 'prod' ? 'Prod' : 'Post';
            // leads[] carries every distinct lead (items mode); legacy has one.
            // Each entry has its own earned — d.leadEarned is the dept SUM.
            const leads = d.leads?.length ? d.leads : (d.lead ? [d.lead] : []);
            leads.forEach((lead) => {
              personTotals[lead.name] = (personTotals[lead.name] || 0) + lead.earned;
              if (!personTags[lead.name]) personTags[lead.name] = [];
              personTags[lead.name].push({ label: `${deptLabel} LEAD`, chipType: 'lead' });
              if (lead.expenseDeduction > 0) personExpenseDeductions[lead.name] = (personExpenseDeductions[lead.name] || 0) + lead.expenseDeduction;
            });
            if (d.lead) {
              // Budget context only meaningful for a single dept-wide lead
              if (!personHoursTracking[d.lead.name]) personHoursTracking[d.lead.name] = {};
              personHoursTracking[d.lead.name].isLead = true;
              personHoursTracking[d.lead.name].budget = (personHoursTracking[d.lead.name].budget || 0) + (d.budget || 0);
              personHoursTracking[d.lead.name].hourlyTotal = (personHoursTracking[d.lead.name].hourlyTotal || 0) + (d.hourlyTotal || 0);
              personHoursTracking[d.lead.name].overrun = (personHoursTracking[d.lead.name].overrun || 0) + (d.overrun || 0);
            }
            d.hourly.forEach((h) => {
              personTotals[h.name] = (personTotals[h.name] || 0) + h.earned;
              if (!personTags[h.name]) personTags[h.name] = [];
              personTags[h.name].push({ label: `${deptLabel} HOURLY`, chipType: 'hourly' });
              if (h.isEstimated) personHasEst[h.name] = true;
              // Track estimated vs logged hours
              const est = parseFloat(h.estHours) || 0;
              const logged = loggedByPerson[(h.name || '').toLowerCase()] || 0;
              if (!personHoursTracking[h.name]) personHoursTracking[h.name] = {};
              personHoursTracking[h.name].estimated = (personHoursTracking[h.name].estimated || 0) + est;
              personHoursTracking[h.name].logged = logged;
              personHoursTracking[h.name].isHourly = true;
            });
          });
          const sortedPersons = Object.entries(personTotals).sort((a, b) => b[1] - a[1]);
          const daysAgo = ns.savedAt ? Math.round((Date.now() - new Date(ns.savedAt).getTime()) / 86400000) : null;
          const hasEstimates = Object.keys(personHasEst).length > 0;
          const snapshotRevenue = ns.totalRevenue || 0;
          const currentRevenue = parseFloat(job.revenue) || 0;
          const isStale = snapshotRevenue > 0 && currentRevenue > 0 && Math.abs(snapshotRevenue - currentRevenue) > 1;
          const snapshotExpenses = ns.expenseMeta?.totalExpenses || 0;
          const currentExpenses = jobExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
          // Staycation splits have no expense concept (labor vs revenue only),
          // so a live job expense must not raise a permanent, unclearable warning.
          const isExpenseStale = ns.payMode !== 'staycation'
            && (snapshotExpenses > 0 || currentExpenses > 0) && Math.abs(snapshotExpenses - currentExpenses) > 1;

          return (
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>Calculator Snapshot</Typography>
                  {hasEstimates && <Chip size="small" label="EST" sx={{ fontSize: '0.5rem', height: 16, fontFamily: 'monospace', fontWeight: 700, bgcolor: 'warning.main', color: 'warning.contrastText' }} />}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  {daysAgo !== null && <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.6rem' }}>saved {daysAgo}d ago</Typography>}
                  <Button size="small" onClick={() => navigate(`/calculator?jobId=${job.id}`)} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>↻ Update Split</Button>
                </Stack>
              </Stack>
              {isStale && (
                <Box sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: 'warning.main', color: 'warning.contrastText', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                  ⚠ Revenue changed ({fmtC(snapshotRevenue)} → {fmtC(currentRevenue)}) — re-run split
                </Box>
              )}
              {isExpenseStale && (
                <Box sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: '#c67a1e', color: '#fff', fontSize: '0.65rem', fontFamily: 'monospace' }}>
                  ⚠ Expenses changed ({fmtC(snapshotExpenses)} → {fmtC(currentExpenses)}) — re-run split
                </Box>
              )}
              <Box sx={{ display: 'flex', borderRadius: 1, overflow: 'hidden', height: 8, mb: 1 }}>
                {roles.map((r) => { const pct = ns.splits[r] || 0; return pct > 0 ? <Box key={r} sx={{ flex: pct, bgcolor: ROLE_COLORS[r] }} /> : null; })}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                {roles.map((r) => `${ROLE_LABELS[r]} ${fmtP(ns.splits[r])} → ${fmtC(ns.amounts[r] || 0)}`).join(' · ')}
              </Typography>
              {/* Per-item allocation breakdown (items mode) */}
              {ns.payMode === 'items' && (ns.itemAllocs || []).length > 0 && (
                <Stack spacing={0.25} sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                  {(ns.itemAllocs || []).map((a) => {
                    const parts = [];
                    if (a.shoot?.lead && a.edit?.lead && a.shoot.lead.name === a.edit.lead.name) {
                      parts.push(`${a.shoot.lead.name} ${fmtC((a.shoot.leadEarned || 0) + (a.edit.leadEarned || 0))}`);
                    } else {
                      if (a.shoot?.lead) parts.push(`${a.shoot.lead.name} (prod) ${fmtC(a.shoot.leadEarned || 0)}`);
                      if (a.edit?.lead) parts.push(`${a.edit.lead.name} (post) ${fmtC(a.edit.leadEarned || 0)}`);
                    }
                    return (
                      <Stack key={a.key} direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.desc}
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: parts.length ? 'text.primary' : 'text.disabled', flexShrink: 0 }}>
                          {parts.length ? parts.join(' · ') : `→ Company ${fmtC(a.budget || 0)}`}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              {sortedPersons.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                  {sortedPersons.map(([name, amt]) => {
                    const tags = personTags[name] || [];
                    const isEst = personHasEst[name];
                    const ht = personHoursTracking[name];
                    // Hours tracking line for hourly crew
                    let hoursLine = null;
                    if (ht?.isHourly && ht.estimated > 0) {
                      const remaining = ht.estimated - ht.logged;
                      const pct = ht.estimated > 0 ? (ht.logged / ht.estimated) * 100 : 0;
                      const hColor = pct > 100 ? 'error.main' : pct > 80 ? 'warning.main' : 'success.main';
                      // Focus hides only the logging readout; the planned hours stay.
                      hoursLine = isHidden('job.logTime') && !(ht.logged > 0) ? (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: hColor, ml: 4.5 }}>
                          ⏱ {ht.estimated.toFixed(1)}h est
                        </Typography>
                      ) : (
                        <Typography data-focus="job.logTime" variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: hColor, ml: 4.5 }}>
                          ⏱ {ht.logged.toFixed(1)}h logged / {ht.estimated.toFixed(1)}h est
                          {remaining > 0 ? ` — ${remaining.toFixed(1)}h remaining` : remaining < 0 ? ` — ${Math.abs(remaining).toFixed(1)}h overrun` : ' — complete'}
                        </Typography>
                      );
                    } else if (ht?.isLead && ht.overrun > 0) {
                      hoursLine = (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'error.main', ml: 4.5 }}>
                          ⚠ Budget overrun: hourly costs exceed dept budget by {fmtC(ht.overrun)}
                        </Typography>
                      );
                    }
                    return (
                      <Box key={name}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ cursor: 'pointer', py: 0.25, '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 0.5 }} onClick={() => { const match = allContacts.find((c) => c.name === name); if (match) navigateToContact(navigate, match.id); }}>
                          <UserAvatar name={name} color={getUserColor(roster.find(r => r.name === name) || { name })} size="md" />
                          <Typography variant="body2" sx={{ flex: 1, textDecoration: 'underline dotted', textUnderlineOffset: 3, fontSize: '0.8rem' }}>{name}</Typography>
                          {tags.map((tag, i) => (
                            <Chip key={i} size="small" label={tag.label} sx={{ fontSize: '0.5rem', height: 16, fontFamily: 'monospace', fontWeight: 700, bgcolor: tag.chipType === 'lead' ? 'primary.main' : 'text.disabled', color: tag.chipType === 'lead' ? 'primary.contrastText' : 'background.paper' }} />
                          ))}
                          <Typography variant="body2" sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1rem', fontWeight: 700, flexShrink: 0, fontStyle: isEst ? 'italic' : 'normal', opacity: isEst ? 0.7 : 1 }}>
                            {fmtC(amt)}{isEst ? ' ~' : ''}
                          </Typography>
                        </Stack>
                        {hoursLine}
                        {/* Expense deductions affecting this person's pay */}
                        {(() => {
                          const ded = personExpenseDeductions[name];
                          if (!ded || ded <= 0) return null;
                          // Find matching expenses from live data by department
                          const deptMap = { 'Orig': 'originator', 'Prod LEAD': 'production', 'Post LEAD': 'post_production' };
                          const depts = new Set();
                          (personTags[name] || []).forEach(t => { if (deptMap[t.label]) depts.add(deptMap[t.label]); });
                          const matchedExpenses = jobExpenses.filter(e => depts.has(e.department));
                          return (
                            <Box sx={{ ml: 4.5, mt: 0.25 }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'warning.main' }}>
                                💸 −{fmtC(ded)} expenses (pre-deduction: {fmtC(amt + ded)})
                              </Typography>
                              {matchedExpenses.length > 0 && (
                                <Stack spacing={0} sx={{ mt: 0.25 }}>
                                  {matchedExpenses.map(exp => (
                                    <Stack key={exp.id} direction="row" spacing={1} alignItems="center" sx={{ pl: 1.5 }}>
                                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.55rem', color: 'text.disabled', flex: 1 }}>
                                        • {exp.description || 'Expense'}
                                      </Typography>
                                      {exp.category && <Chip size="small" label={exp.category} variant="outlined" sx={{ fontSize: '0.45rem', height: 14, opacity: 0.6 }} />}
                                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.55rem', color: 'text.disabled', flexShrink: 0 }}>
                                        {fmtC(exp.amount)}
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Stack>
                              )}
                            </Box>
                          );
                        })()}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          );
        })()}

      </Box>

      {/* ═══ TIME ENTRIES DIALOG ═══ */}
      <Dialog open={showTimeDialog} onClose={() => { setShowTimeDialog(false); setShowTimeForm(false); setEditingTimeEntry(null); }} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>{isTimeAdmin ? 'Time Entries' : 'My Hours'}</Typography>
          <Button variant="contained" size="small" onClick={() => { setEditingTimeEntry(null); setShowTimeForm(!showTimeForm); }} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>{showTimeForm ? 'Cancel' : '+ Log Time'}</Button>
        </DialogTitle>
        <DialogContent dividers>
          {!isTimeAdmin && myHourly.isHourly && (() => {
            // Compare like with like: only Shoot/Edit hours are paid against the
            // dept estimate (Travel → mileage, Meeting/Admin/Other → admin pool)
            const logged = visibleTimeEntries
              .filter((e) => e.category === 'Shoot' || e.category === 'Edit')
              .reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
            const est = myHourly.estHours;
            const pct = est > 0 ? (logged / est) * 100 : 0;
            const color = pct > 100 ? 'error.main' : pct > 80 ? 'warning.main' : 'text.secondary';
            return (
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color, fontWeight: 600 }}>
                ⏱ {logged.toFixed(1)}h logged{est > 0 && <> of ~{est}h estimated{pct > 100 ? ' — over estimate' : ''}</>}
              </Typography>
            );
          })()}
          {(showTimeForm || editingTimeEntry) && (
            <Box sx={{ mb: 2 }}>
              <TimeEntryForm
                defaultJobId={job.id}
                initialEntry={editingTimeEntry}
                onSuccess={() => { setShowTimeForm(false); setEditingTimeEntry(null); }}
                onCancel={() => { setShowTimeForm(false); setEditingTimeEntry(null); }}
              />
            </Box>
          )}
          {visibleTimeEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>{isTimeAdmin ? 'No time entries for this job' : 'No hours logged yet — hit + Log Time'}</Typography>
          ) : (
            <>
              <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Date</TableCell>{isTimeAdmin && <TableCell>Person</TableCell>}<TableCell>Category</TableCell><TableCell>Dept</TableCell><TableCell align="right">Hours</TableCell><TableCell align="center" />
                </TableRow></TableHead>
                <TableBody>
                  {visibleTimeEntries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>{fmtD(entry.date)}</TableCell>
                      {isTimeAdmin && <TableCell sx={{ fontWeight: 600 }}>{entry.person || '—'}</TableCell>}
                      <TableCell><Chip size="small" label={entry.category || '—'} variant="outlined" sx={{ fontSize: '0.65rem' }} /></TableCell>
                      <TableCell>{entry.department ? <Chip size="small" label={entry.department === 'Post Production' ? 'Post' : entry.department === 'Production' ? 'Prod' : 'Orig'} sx={{ fontSize: '0.55rem', height: 18, bgcolor: 'primary.main', color: 'primary.contrastText' }} /> : '—'}</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>{entry.hours}h</TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => { setShowTimeForm(false); setEditingTimeEntry(entry); }} sx={{ fontSize: '0.65rem', textTransform: 'none' }}>Edit</Button>
                        <Button size="small" color="error" onClick={async () => { if (!confirm('Delete this time entry?')) return; try { await deleteTimeEntry.mutateAsync(entry.id); if (editingTimeEntry?.id === entry.id) setEditingTimeEntry(null); toast('Time entry deleted'); } catch (err) { toast(err?.message ? `Failed to delete: ${err.message}` : 'Failed to delete', 'error'); } }} sx={{ fontSize: '0.65rem', textTransform: 'none' }}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Total:</Typography>
                <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>{visibleTimeEntries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0).toFixed(1)}h</Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => { setShowTimeDialog(false); setShowTimeForm(false); setEditingTimeEntry(null); }}>Close</Button></DialogActions>
      </Dialog>

      {/* ═══ EXPENSES DIALOG ═══ */}
      <Dialog open={showExpenseDialog} onClose={() => { setShowExpenseDialog(false); setShowExpenseForm(false); }} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>Expenses</Typography>
          <Button variant="contained" size="small" onClick={() => setShowExpenseForm(!showExpenseForm)} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>{showExpenseForm ? 'Cancel' : '+ Add Expense'}</Button>
        </DialogTitle>
        <DialogContent dividers>
          {showExpenseForm && <Box sx={{ mb: 2 }}><ExpenseForm initialExpense={{ jobId: job.id }} onSave={() => setShowExpenseForm(false)} onCancel={() => setShowExpenseForm(false)} /></Box>}
          {jobExpenses.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No expenses for this job</Typography>
          ) : (
            <>
              <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Date</TableCell><TableCell>Person</TableCell><TableCell>Category</TableCell><TableCell>Description</TableCell><TableCell align="right">Amount</TableCell><TableCell align="center" />
                </TableRow></TableHead>
                <TableBody>
                  {jobExpenses.map((exp) => (
                    <TableRow key={exp.id} hover>
                      <TableCell>{fmtD(exp.date)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{exp.person || '—'}</TableCell>
                      <TableCell><Chip size="small" label={exp.category || '—'} variant="outlined" sx={{ fontSize: '0.65rem' }} /></TableCell>
                      <TableCell>{exp.description || '—'}</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>{fmtC(exp.amount)}</TableCell>
                      <TableCell align="center">
                        <Button size="small" color="error" onClick={async () => { if (!confirm('Delete this expense?')) return; try { await deleteExpense.mutateAsync(exp.id); toast('Expense deleted'); } catch { toast('Failed to delete', 'error'); } }} sx={{ fontSize: '0.65rem', textTransform: 'none' }}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Total:</Typography>
                <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>{fmtC(jobExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0))}</Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => { setShowExpenseDialog(false); setShowExpenseForm(false); }}>Close</Button></DialogActions>
      </Dialog>

      {showLinkModal && (
        <LinkInvoiceModal job={job} onClose={() => setShowLinkModal(false)} onLinked={() => onRefresh?.()} />
      )}
    </Box>
  );
}
